import { useMemo } from 'react';
import { useIntl } from '../reactIntl';

/**
 * Hook to get translated bio text for a user based on current locale
 * 
 * @param {Object} user - The user object
 * @param {Object} user.attributes - User attributes
 * @param {Object} user.attributes.profile - User profile
 * @param {string} user.attributes.profile.bio - Original bio text (source language)
 * @param {Object} user.attributes.profile.protectedData - Protected data with translations
 * @param {Object} user.attributes.profile.protectedData.bioTranslations - Bio translations by language
 * @returns {string} Translated bio text or original bio if translation not available
 * 
 * @example
 * const translatedBio = useTranslatedBio(user);
 * // Returns translated bio based on current locale
 * // Falls back to original bio if translation doesn't exist
 */
export const useTranslatedBio = (user) => {
  const intl = useIntl();
  const currentLocale = intl.locale || 'sv';

  return useMemo(() => {
    if (!user?.attributes?.profile) {
      return '';
    }

    const profile = user.attributes.profile;
    const originalBio = profile.bio || '';
    
    // Source language (Swedish) - return original
    if (currentLocale === 'sv') {
      return originalBio;
    }

    // Try to get translated bio from protectedData
    const bioTranslations = profile.protectedData?.bioTranslations || {};
    const translatedBio = bioTranslations[currentLocale];

    // Return translated bio if available, otherwise fall back to original
    return translatedBio || originalBio;
  }, [user, currentLocale]);
};

/**
 * Hook to check if bio translations exist for a user
 * 
 * @param {Object} user - The user object
 * @returns {boolean} True if translations exist
 */
export const useHasBioTranslations = (user) => {
  return useMemo(() => {
    const bioTranslations = user?.attributes?.profile?.protectedData?.bioTranslations;
    return bioTranslations && Object.keys(bioTranslations).length > 0;
  }, [user]);
};
